import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Save, ExternalLink, Eye, X, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 9);

const BLOCK_TYPES = [
  { type: "hero", label: "Hero" }, { type: "text", label: "Text" }, { type: "image", label: "Image" },
  { type: "features", label: "Features" }, { type: "cta_banner", label: "CTA banner" },
  { type: "gallery", label: "Gallery" }, { type: "embed", label: "Video embed" },
  { type: "quote", label: "Quote" }, { type: "spacer", label: "Spacer" },
];
const NEW_BLOCK = {
  hero: { eyebrow: "", heading: "New hero heading", subheading: "", image: "", cta_label: "", cta_href: "" },
  text: { heading: "", body: "" },
  image: { url: "", caption: "", full_width: false },
  features: { heading: "", items: [{ icon: "Sparkles", title: "Feature", text: "" }] },
  cta_banner: { heading: "Ready to begin?", subheading: "", button_label: "Get started", button_href: "/join" },
  gallery: { images: [] },
  embed: { url: "" },
  quote: { quote: "", author: "" },
  spacer: { height: 48 },
};

export default function PageBuilder() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // page object being edited, or null
  const { data: pages = [], isLoading } = useQuery({ queryKey: ["admin-pages"], queryFn: () => api.get("/admin/pages").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-pages"] });

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast.success("Page deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (editing) return <PageEditor page={editing} onClose={() => { setEditing(null); refresh(); }} />;

  return (
    <div data-testid="admin-pages-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Layout size={22} className="text-[#144F75]" /> Page Builder</h2>
          <p className="text-sm text-[#4A5D6B]">Compose pages from reusable blocks — no developer needed. Published pages live at <code>/p/&lt;slug&gt;</code>.</p>
        </div>
        <button onClick={() => setEditing({ title: "", sections: [], visibility: "public", published: false, seo_title: "", seo_description: "", show_in_nav: false, nav_label: "" })} data-testid="page-new" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New page</button>
      </div>

      {isLoading ? <p className="text-sm text-[#4A5D6B]"><Loader2 className="animate-spin inline" size={16} /> Loading…</p> : (
        <>
        <div className="eyw-card overflow-hidden hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F1EB] text-[#4A5D6B]"><tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Blocks</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {pages.map((p) => (
                <tr key={p.id} data-testid={`page-row-${p.id}`} className="hover:bg-[#F9F7F3]">
                  <td className="px-4 py-3 text-[#1A242B]">{p.title}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">/p/{p.slug}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{p.sections?.length || 0}</td>
                  <td className="px-4 py-3">{p.published ? <span className="text-xs text-[#5C8A72]">Published</span> : <span className="text-xs text-[#B5794E]">Draft</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {p.published && <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#144F75] mr-3 inline-block" title="View"><ExternalLink size={15} /></a>}
                    <button onClick={() => setEditing(p)} data-testid={`page-edit-${p.id}`} className="text-[#144F75] hover:underline text-xs font-medium mr-3">Edit</button>
                    <button onClick={() => del(p)} data-testid={`page-delete-${p.id}`} className="text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {pages.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#4A5D6B]">No pages yet. Create your first page.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-3">
          {pages.map((p) => (
            <div key={p.id} data-testid={`page-card-${p.id}`} className="eyw-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[#1A242B] font-medium break-words">{p.title}</p>
                  <p className="text-xs text-[#8FA89B] break-all">/p/{p.slug} · {p.sections?.length || 0} blocks</p>
                </div>
                {p.published ? <span className="text-xs text-[#5C8A72] shrink-0">Published</span> : <span className="text-xs text-[#B5794E] shrink-0">Draft</span>}
              </div>
              <div className="mt-3 flex items-center gap-4">
                {p.published && <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#144F75] inline-flex items-center gap-1 text-xs" title="View"><ExternalLink size={14} /> View</a>}
                <button onClick={() => setEditing(p)} data-testid={`page-edit-${p.id}`} className="text-[#144F75] hover:underline text-xs font-medium">Edit</button>
                <button onClick={() => del(p)} data-testid={`page-delete-${p.id}`} className="text-red-500 hover:text-red-600 inline-flex items-center gap-1 text-xs"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
          {pages.length === 0 && <p className="px-4 py-8 text-center text-[#4A5D6B] text-sm eyw-card">No pages yet. Create your first page.</p>}
        </div>
        </>
      )}
    </div>
  );
}

function PageEditor({ page, onClose }) {
  const [form, setForm] = useState({ ...page, sections: page.sections?.map((s) => ({ ...s, id: s.id || uid() })) || [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (id, props) => set("sections", form.sections.map((s) => s.id === id ? { ...s, props } : s));
  const addBlock = (type) => set("sections", [...form.sections, { id: uid(), type, props: JSON.parse(JSON.stringify(NEW_BLOCK[type])) }]);
  const removeBlock = (id) => set("sections", form.sections.filter((s) => s.id !== id));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= form.sections.length) return;
    const arr = [...form.sections]; [arr[i], arr[j]] = [arr[j], arr[i]]; set("sections", arr);
  };

  const save = async (publish) => {
    if (!form.title.trim()) { toast.error("Give the page a title"); return; }
    setSaving(true);
    const body = { ...form, published: publish != null ? publish : form.published };
    try {
      let saved;
      if (form.id) saved = (await api.put(`/admin/pages/${form.id}`, body)).data;
      else saved = (await api.post("/admin/pages", body)).data;
      setForm({ ...saved, sections: saved.sections?.map((s) => ({ ...s, id: s.id || uid() })) || [] });
      toast.success(body.published ? "Published" : "Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="page-editor">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <button onClick={onClose} className="text-[#144F75] text-sm hover:underline">← All pages</button>
        <div className="flex gap-2">
          {form.id && form.published && <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" className="eyw-btn-ghost !py-2 text-sm"><Eye size={15} /> View</a>}
          <button onClick={() => save(false)} disabled={saving} data-testid="page-save-draft" className="eyw-btn-ghost !py-2 text-sm">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save draft</button>
          <button onClick={() => save(true)} disabled={saving} data-testid="page-publish" className="eyw-btn !py-2 text-sm">Publish</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Settings */}
        <div className="space-y-3">
          <div className="eyw-card p-4 space-y-3">
            <div><label className="text-xs text-[#4A5D6B]">Page title</label><input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="page-title" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Slug (optional)</label><input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="page-slug" placeholder="auto from title" className="eyw-input w-full text-sm" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Visibility</label>
              <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="page-visibility" className="eyw-input w-full">
                {["public", "discovery", "community", "enhancer", "private"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <label className="flex items-center justify-between text-sm text-[#1A242B]"><span>Show in nav</span><input type="checkbox" checked={form.show_in_nav} onChange={(e) => set("show_in_nav", e.target.checked)} data-testid="page-shownav" /></label>
            {form.show_in_nav && <input value={form.nav_label} onChange={(e) => set("nav_label", e.target.value)} placeholder="Nav label" className="eyw-input w-full text-sm" />}
          </div>
          <div className="eyw-card p-4 space-y-3">
            <p className="text-xs font-semibold text-[#1A242B]">SEO</p>
            <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="page-seo-title" placeholder="SEO title" className="eyw-input w-full text-sm" />
            <textarea rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="page-seo-desc" placeholder="SEO meta description" className="eyw-input w-full text-sm resize-none" />
          </div>
          <div className="eyw-card p-4">
            <p className="text-xs font-semibold text-[#1A242B] mb-2">Add a block</p>
            <div className="flex flex-wrap gap-1.5">
              {BLOCK_TYPES.map((b) => <button key={b.type} onClick={() => addBlock(b.type)} data-testid={`add-block-${b.type}`} className="text-xs px-2.5 py-1.5 rounded-lg border border-[#E5E0D8] hover:border-[#144F75] hover:text-[#144F75]">+ {b.label}</button>)}
            </div>
          </div>
        </div>

        {/* Section list */}
        <div className="space-y-3" data-testid="page-sections">
          {form.sections.length === 0 && <div className="eyw-card p-10 text-center text-[#4A5D6B]">No blocks yet — add one from the left.</div>}
          {form.sections.map((s, i) => (
            <div key={s.id} className="eyw-card p-4" data-testid={`block-${s.type}-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#144F75]">{s.type}</span>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[#4A5D6B] disabled:opacity-30" data-testid={`block-up-${i}`}><ChevronUp size={16} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === form.sections.length - 1} className="p-1 text-[#4A5D6B] disabled:opacity-30" data-testid={`block-down-${i}`}><ChevronDown size={16} /></button>
                  <button onClick={() => removeBlock(s.id)} className="p-1 text-red-500" data-testid={`block-remove-${i}`}><Trash2 size={15} /></button>
                </div>
              </div>
              <BlockEditor section={s} onChange={(props) => setSection(s.id, props)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const T = ({ k, label, area, p, set }) => area
    ? <div><label className="text-xs text-[#4A5D6B]">{label}</label><textarea rows={3} value={p[k] || ""} onChange={(e) => set(k, e.target.value)} className="eyw-input w-full text-sm resize-none" /></div>
    : <div><label className="text-xs text-[#4A5D6B]">{label}</label><input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} className="eyw-input w-full text-sm" /></div>;

function BlockEditor({ section, onChange }) {
  const p = section.props || {};
  const set = (k, v) => onChange({ ...p, [k]: v });
  const tp = { p, set };

  switch (section.type) {
    case "hero":
      return <div className="space-y-2"><T {...tp} k="eyebrow" label="Eyebrow" /><T {...tp} k="heading" label="Heading" /><T {...tp} k="subheading" label="Subheading" area /><ImageUpload value={p.image} onChange={(u) => set("image", u)} label="Background image" testid="block-hero-img" /><div className="grid grid-cols-2 gap-2"><T {...tp} k="cta_label" label="Button label" /><T {...tp} k="cta_href" label="Button link" /></div></div>;
    case "text":
      return <div className="space-y-2"><T {...tp} k="heading" label="Heading" /><T {...tp} k="body" label="Body" area /></div>;
    case "image":
      return <div className="space-y-2"><ImageUpload value={p.url} onChange={(u) => set("url", u)} label="Image" testid="block-image" /><T {...tp} k="caption" label="Caption" /><label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={!!p.full_width} onChange={(e) => set("full_width", e.target.checked)} /> Full width</label></div>;
    case "cta_banner":
      return <div className="space-y-2"><T {...tp} k="heading" label="Heading" /><T {...tp} k="subheading" label="Subheading" area /><div className="grid grid-cols-2 gap-2"><T {...tp} k="button_label" label="Button label" /><T {...tp} k="button_href" label="Button link" /></div></div>;
    case "embed":
      return <T {...tp} k="url" label="YouTube / Vimeo / Spotify URL" />;
    case "quote":
      return <div className="space-y-2"><T {...tp} k="quote" label="Quote" area /><T {...tp} k="author" label="Author" /></div>;
    case "spacer":
      return <div><label className="text-xs text-[#4A5D6B]">Height (px)</label><input type="number" value={p.height || 48} onChange={(e) => set("height", Number(e.target.value))} className="eyw-input w-24 text-sm" /></div>;
    case "gallery":
      return <GalleryEditor images={p.images || []} onChange={(imgs) => set("images", imgs)} />;
    case "features":
      return <FeaturesEditor heading={p.heading || ""} items={p.items || []} onChange={(items, heading) => onChange({ ...p, items, heading })} />;
    default:
      return null;
  }
}

function GalleryEditor({ images, onChange }) {
  return (
    <div className="space-y-2">
      {images.map((url, i) => (
        <div key={i} className="flex items-center gap-2">
          <img src={url} alt="" className="h-10 w-10 rounded object-cover" />
          <button onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="text-red-500 text-xs">Remove</button>
        </div>
      ))}
      <ImageUpload value="" onChange={(u) => u && onChange([...images, u])} label="Add image" testid="gallery-add" />
    </div>
  );
}

function FeaturesEditor({ heading, items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it), heading);
  return (
    <div className="space-y-2">
      <div><label className="text-xs text-[#4A5D6B]">Section heading</label><input value={heading} onChange={(e) => onChange(items, e.target.value)} className="eyw-input w-full text-sm" /></div>
      {items.map((it, i) => (
        <div key={i} className="border border-[#E5E0D8] rounded-lg p-2 space-y-1.5">
          <div className="grid grid-cols-2 gap-2">
            <input value={it.icon} onChange={(e) => upd(i, "icon", e.target.value)} placeholder="Icon (lucide name)" className="eyw-input text-xs" />
            <input value={it.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Title" className="eyw-input text-xs" />
          </div>
          <input value={it.text} onChange={(e) => upd(i, "text", e.target.value)} placeholder="Text" className="eyw-input text-xs w-full" />
          <button onClick={() => onChange(items.filter((_, idx) => idx !== i), heading)} className="text-red-500 text-xs">Remove</button>
        </div>
      ))}
      <button onClick={() => onChange([...items, { icon: "Sparkles", title: "Feature", text: "" }], heading)} className="text-xs text-[#144F75]">+ Add feature</button>
    </div>
  );
}
